import React, { useState } from "react";
import DOMPurify from "dompurify";
import {
  Box,
  Card,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
export function previewMerge(html: string, sample: Record<string, string>) {
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (original, key: string) =>
    key in sample
      ? sample[key].replace(
          /[&<>"']/g,
          (char) =>
            ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              '"': "&quot;",
              "'": "&#39;",
            })[char]!,
        )
      : original,
  );
}
export function EmailPreview({
  html,
  subject = "",
  preheader = "",
  sampleControls = true,
}: {
  html: string;
  subject?: string;
  preheader?: string;
  sampleControls?: boolean;
}) {
  const [width, setWidth] = useState("desktop");
  const [first, setFirst] = useState("Avery");
  const [last, setLast] = useState("Homeowner");
  const [email, setEmail] = useState("avery@example.com");
  const [company, setCompany] = useState("Example Company");
  const values = {
    first_name: first,
    last_name: last,
    full_name: `${first} ${last}`,
    email,
    company_name: company,
  };
  const plain = (text: string) =>
    text.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (original, key: string) => values[key as keyof typeof values] ?? original,
    );
  const clean = DOMPurify.sanitize(previewMerge(html, values));
  const srcDoc = `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline';"><style>body{margin:0;padding:16px;font-family:Arial,sans-serif;color:#172033}img{max-width:100%;height:auto}table{max-width:100%}*{box-sizing:border-box}</style></head><body>${clean}<p style="font-size:12px;color:#64748b">An unsubscribe link is added during delivery.</p></body></html>`;
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography level="title-md">Email preview</Typography>
        <Select
          aria-label="Preview width"
          value={width}
          onChange={(_, value) => setWidth(value || "desktop")}
        >
          <Option value="desktop">Desktop</Option>
          <Option value="mobile">Mobile</Option>
        </Select>
      </Stack>
      {sampleControls && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2,minmax(0,1fr))",
              md: "repeat(2,minmax(0,1fr))",
            },
            gap: 1,
          }}
        >
          {[
            ["First name", first, setFirst],
            ["Last name", last, setLast],
            ["Email", email, setEmail],
            ["Company", company, setCompany],
          ].map(([label, value, setter]) => (
            <FormControl key={String(label)} sx={{ minWidth: 0 }}>
              <FormLabel>{String(label)}</FormLabel>
              <Input
                value={String(value)}
                onChange={(event) =>
                  (setter as (value: string) => void)(event.target.value)
                }
              />
            </FormControl>
          ))}
        </Box>
      )}
      <Card variant="soft">
        <Typography level="title-sm">
          {plain(subject) || "Subject preview"}
        </Typography>
        <Typography level="body-sm">
          {plain(preheader) ||
            "Add inbox preview text to support your subject."}
        </Typography>
      </Card>
      <Box
        sx={{
          maxWidth: "100%",
          width: width === "mobile" ? 375 : "100%",
          mx: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "md",
          overflow: "hidden",
        }}
      >
        <Box
          component="iframe"
          title="Rendered email preview"
          sandbox=""
          srcDoc={srcDoc}
          sx={{ display: "block", width: "100%", height: 430, border: 0 }}
        />
      </Box>
      <Typography level="body-xs">
        Sample merge values are for preview only. Email applications may render
        layouts differently.
      </Typography>
    </Stack>
  );
}
